import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import {
  ArrowRight,
  Search,
  MessageCircleQuestion,
  BookOpen,
  ArrowUpRight,
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { AffiliateStrip } from '../components/AffiliateStrip';
import SEO from '../components/SEO';
import { useT } from '../i18n';
import {
  KB_CATEGORIES,
  buildKnowledgeBase,
  groupByCategory,
} from '../data/knowledgeBase';

const copy = {
  en: {
    eyebrow: 'KITXENS KNOWLEDGE BASE',
    title: 'Every answer, in one place.',
    body: 'The Kitxens Knowledge Base aggregates every question our restaurant operators ask us — across POS, digital branding, AI, revenue growth and analytics. It grows automatically as we ship new services.',
    search: 'Search the knowledge base — try "no-show", "Toast POS", "Google reviews"…',
    noResults: 'No answers match',
    all: 'All',
    questions: 'questions',
    fromLabel: 'From',
    stillTitle: 'Still not finding the answer?',
    stillBody: 'A Kitxens specialist will reply personally within 24 hours — no bots, no forms lost.',
    cta: 'Talk to a Specialist',
    metaTitle: 'Restaurant Knowledge Base — FAQs | Kitxens',
    metaDesc:
      'The central Kitxens knowledge base. Answers on POS, AI agents, digital branding, revenue growth, delivery, catering, reviews and analytics for restaurants.',
  },
  es: {
    eyebrow: 'BASE DE CONOCIMIENTO KITXENS',
    title: 'Todas las respuestas, en un solo lugar.',
    body: 'La Base de Conocimiento de Kitxens agrupa todas las preguntas que nos hacen los operadores de restaurantes — sobre POS, branding digital, IA, crecimiento e analítica. Crece automáticamente a medida que lanzamos nuevos servicios.',
    search: 'Busca en la base — prueba "no-show", "Toast POS", "reseñas Google"…',
    noResults: 'Ninguna respuesta coincide con',
    all: 'Todo',
    questions: 'preguntas',
    fromLabel: 'De',
    stillTitle: '¿Aún no encuentras la respuesta?',
    stillBody: 'Un especialista de Kitxens te responderá personalmente en menos de 24 horas — sin bots, sin formularios perdidos.',
    cta: 'Hablar con un Especialista',
    metaTitle: 'Base de Conocimiento — FAQs para Restaurantes | Kitxens',
    metaDesc:
      'La base de conocimiento central de Kitxens. Respuestas sobre POS, agentes IA, branding digital, crecimiento, delivery, catering, reseñas y analítica.',
  },
};

// Normalize a string for search (strip diacritics + lowercase).
const norm = (s = '') =>
  s
    .toString()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();

export default function FaqPage() {
  const { lang } = useT();
  const c = copy[lang] || copy.en;
  const [query, setQuery] = useState('');
  const [activeCat, setActiveCat] = useState('all');

  // Build & filter knowledge base once per lang / query / cat change.
  const all = useMemo(() => buildKnowledgeBase(), []);
  const q = norm(query.trim());

  const filtered = useMemo(() => {
    return all.filter((e) => {
      if (activeCat !== 'all' && e.category !== activeCat) return false;
      if (!q) return true;
      const hay = norm(`${e.q[lang] || e.q.en} ${e.a[lang] || e.a.en} ${e[`sourceLabel_${lang}`] || e.sourceLabel_en}`);
      return hay.includes(q);
    });
  }, [all, q, activeCat, lang]);

  const groups = useMemo(() => groupByCategory(filtered), [filtered]);
  const counts = useMemo(() => {
    const c2 = { all: all.length };
    all.forEach((e) => {
      c2[e.category] = (c2[e.category] || 0) + 1;
    });
    return c2;
  }, [all]);

  // FAQPage JSON-LD schema for SEO — uses the FIRST 60 entries from the
  // filtered view so the schema always reflects what the page actually shows.
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: filtered.slice(0, 60).map((e) => ({
      '@type': 'Question',
      name: e.q[lang] || e.q.en,
      acceptedAnswer: {
        '@type': 'Answer',
        text: e.a[lang] || e.a.en,
      },
    })),
  };

  return (
    <div className="pt-28">
      <SEO
        path="/faq"
        title={c.metaTitle}
        description={c.metaDesc}
        schema={schema}
      />

      {/* Hero + search */}
      <section className="bg-gradient-to-b from-orange-50/60 to-white pt-10 pb-14 md:py-20 lg:py-24">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="inline-flex items-center gap-2 text-[#F77F11] text-[11px] tracking-[0.22em] font-semibold uppercase mb-4">
            <BookOpen className="w-3.5 h-3.5" /> {c.eyebrow}
          </p>
          <h1 className="text-4xl sm:text-5xl font-bold text-neutral-900 leading-[1.05] tracking-[-0.02em]">
            {c.title}
          </h1>
          <p className="mt-5 text-neutral-600 text-[15.5px] leading-relaxed">{c.body}</p>
          <div className="mt-9 relative max-w-xl mx-auto">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={c.search}
              data-testid="faq-search-input"
              className="pl-10 h-12 rounded-xl border-neutral-200 focus-visible:border-[#F77F11] focus-visible:ring-[#F77F11]/20"
            />
          </div>
          <p className="mt-4 text-xs text-neutral-500">
            {all.length} {c.questions}
          </p>
        </div>
      </section>

      {/* Category filters */}
      <section className="sticky top-[68px] z-30 bg-white/90 backdrop-blur border-b border-neutral-100">
        <div className="max-w-5xl mx-auto px-6 py-3 flex flex-wrap gap-2 justify-center">
          <FilterPill
            active={activeCat === 'all'}
            label={c.all}
            count={counts.all}
            onClick={() => setActiveCat('all')}
            testId="faq-filter-all"
          />
          {KB_CATEGORIES.map((cat) => (
            <FilterPill
              key={cat.id}
              active={activeCat === cat.id}
              label={cat[`label_${lang}`] || cat.label_en}
              count={counts[cat.id] || 0}
              onClick={() => setActiveCat(cat.id)}
              testId={`faq-filter-${cat.id}`}
            />
          ))}
        </div>
      </section>

      {/* Grouped answers */}
      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6 space-y-14">
          {groups.map(({ category, entries }) => (
            <div key={category.id} data-testid={`faq-group-${category.id}`}>
              <div className="mb-8">
                <div className="flex items-center gap-3 flex-wrap">
                  <h2 className="text-2xl sm:text-[28px] font-semibold text-neutral-900 tracking-[-0.015em]">
                    {category[`label_${lang}`] || category.label_en}
                  </h2>
                  <span className="inline-flex items-center h-6 px-2.5 rounded-full bg-orange-50 text-[#F77F11] text-[11px] font-semibold tracking-wide">
                    {entries.length}
                  </span>
                </div>
                <p className="mt-2.5 text-neutral-600 text-[14.5px] leading-relaxed max-w-2xl">
                  {category[`description_${lang}`] || category.description_en}
                </p>
              </div>
              <Accordion type="single" collapsible className="space-y-3">
                {entries.map((e) => (
                  <AccordionItem
                    key={e.id}
                    value={e.id}
                    data-testid={`faq-item-${e.id}`}
                    className="group border border-neutral-200 rounded-2xl px-6 py-1 transition-all data-[state=open]:border-[#F77F11]/60 data-[state=open]:shadow-[0_18px_40px_-20px_rgba(247,127,17,0.25)] hover:border-neutral-300"
                  >
                    <AccordionTrigger className="text-left font-medium text-neutral-900 hover:no-underline py-5 text-[15.5px] tracking-[-0.005em] [&[data-state=open]]:text-[#F77F11] [&[data-state=open]]:font-semibold">
                      {e.q[lang] || e.q.en}
                    </AccordionTrigger>
                    <AccordionContent className="text-neutral-600 leading-relaxed pb-6 text-[14.5px]">
                      {e.a[lang] || e.a.en}
                      {e.source === 'service' && (
                        <Link
                          to={e.sourceHref}
                          className="mt-4 inline-flex items-center gap-1 text-[#F77F11] hover:text-[#d8651a] text-[12.5px] font-semibold group/link"
                        >
                          {c.fromLabel}: {e[`sourceLabel_${lang}`] || e.sourceLabel_en}
                          <ArrowUpRight className="w-3.5 h-3.5 transition-transform group-hover/link:translate-x-0.5 group-hover/link:-translate-y-0.5" />
                        </Link>
                      )}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          ))}

          {groups.length === 0 && (
            <div className="text-center py-16">
              <MessageCircleQuestion className="w-10 h-10 text-[#F77F11] mx-auto mb-3" />
              <p className="text-neutral-600">
                {c.noResults} “{query}”.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Talk-to-a-specialist tail */}
      <section className="py-20 bg-gradient-to-b from-white to-orange-50/40">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <h2 className="text-3xl sm:text-4xl font-semibold text-neutral-900 tracking-[-0.015em]">
            {c.stillTitle}
          </h2>
          <p className="mt-4 text-neutral-600 text-[15.5px] leading-relaxed">{c.stillBody}</p>
          <Button
            asChild
            className="mt-7 bg-[#F77F11] hover:bg-[#d8651a] text-white px-6 h-12 rounded-full text-[15px] font-semibold shadow-sm hover:shadow-md transition-all"
          >
            <Link to="/contact#contact-form" data-testid="faq-cta-specialist">
              {c.cta} <ArrowRight className="w-4 h-4 ml-1" />
            </Link>
          </Button>
        </div>
      </section>

      <AffiliateStrip className="pt-10 pb-20" />
    </div>
  );
}

function FilterPill({ active, label, count, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`inline-flex items-center gap-2 px-4 h-9 rounded-full text-[13px] font-semibold transition-all border ${
        active
          ? 'bg-[#F77F11] text-white border-[#F77F11] shadow-sm'
          : 'bg-white text-neutral-700 border-neutral-200 hover:border-[#F77F11] hover:text-[#F77F11]'
      }`}
    >
      <span>{label}</span>
      <span
        className={`inline-flex items-center justify-center h-5 min-w-[22px] px-1.5 rounded-full text-[11px] ${
          active ? 'bg-white/25' : 'bg-neutral-100 text-neutral-600'
        }`}
      >
        {count}
      </span>
    </button>
  );
}
